import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { useProfiles } from "../hooks/useProfiles";
import { usePermissions } from "../hooks/usePermissions";
import { isDefaultAdjustments } from "../utils/imageAdjustments";
import { combineAdjustments, formatAdjustmentSummary } from "../utils/adjustmentUtils";
import ToneCurve from "./ToneCurve";

// Default color grading values
const DEFAULT_COLOR_GRADING = {
  lift: { r: 0, g: 0, b: 0 },
  gamma: { r: 1, g: 1, b: 1 },
  gain: { r: 1, g: 1, b: 1 },
};

/**
 * AdjustmentSlider - Lightroom-style adjustment slider
 *
 * Features:
 * - Center-zero design for bipolar adjustments (e.g., -100 to +100)
 * - Double-click to reset to default
 * - Visual indicator showing distance from center
 */
export default function AdjustmentSlider({
  label,
  value = 0,
  min = -100,
  max = 100,
  step = 1,
  defaultValue = 0,
  onChange,
  disabled = false,
}) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const inputRef = useRef(null);

  const handleChange = (e) => {
    onChange(parseFloat(e.target.value));
  };

  const handleDoubleClick = () => {
    onChange(defaultValue);
  };

  // Format display value: show sign, respect step precision
  const displayValue = useMemo(() => {
    const decimals = step < 1 ? String(step).split(".")[1]?.length || 1 : 0;
    const formatted = decimals > 0 ? Number(value).toFixed(decimals) : String(value);
    return value > 0 ? `+${formatted}` : formatted;
  }, [value, step]);

  // Click on value to edit
  const startEditing = () => {
    if (disabled) return;
    setEditText(String(value));
    setEditing(true);
  };

  // Commit the typed value
  const commitEdit = () => {
    const parsed = parseFloat(editText);
    if (!isNaN(parsed)) {
      // Round to step precision
      const decimals = step < 1 ? String(step).split(".")[1]?.length || 1 : 0;
      const rounded = Number(Math.round(parsed / step) * step);
      const clamped = Math.min(max, Math.max(min, Number(rounded.toFixed(decimals))));
      onChange(clamped);
    }
    setEditing(false);
  };

  const handleEditKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitEdit();
    } else if (e.key === "Escape") {
      setEditing(false);
    }
  };

  // Auto-focus and select on edit start
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  // Calculate fill percentage for visual indicator
  const range = max - min;
  const centerPct = ((defaultValue - min) / range) * 100;
  const valuePct = ((value - min) / range) * 100;

  // Determine fill direction and width
  const isAboveDefault = value >= defaultValue;
  const fillStart = isAboveDefault ? centerPct : valuePct;
  const fillWidth = Math.abs(valuePct - centerPct);

  return (
    <div className={`adjustment-slider ${disabled ? "disabled" : ""}`}>
      <div className="slider-header">
        <span className="slider-label">{label}</span>
        {editing ? (
          <input
            ref={inputRef}
            type="text"
            className="slider-value-input"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={handleEditKeyDown}
          />
        ) : (
          <span
            className="slider-value slider-value-clickable"
            onClick={startEditing}
            title="Click to type a value"
          >
            {displayValue}
          </span>
        )}
      </div>
      <div className="slider-track-wrapper" onDoubleClick={handleDoubleClick}>
        {/* Background track */}
        <div className="slider-track" />
        {/* Center marker for bipolar sliders */}
        {min < 0 && max > 0 && (
          <div
            className="slider-center-mark"
            style={{ left: `${centerPct}%` }}
          />
        )}
        {/* Fill indicator */}
        <div
          className="slider-fill"
          style={{
            left: `${fillStart}%`,
            width: `${fillWidth}%`,
          }}
        />
        {/* Actual input */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handleChange}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

/**
 * DevelopPanel - Complete develop/adjustments panel with tabs and profile support
 *
 * Features:
 * - Tabs: Basic, Advanced, Profiles
 * - Basic: Light, Presence, Color sliders
 * - Advanced: Sharpening, Color Grading, Denoise
 * - Profiles: System/Company/Personal profiles, Import, Set Default
 * - Save as Profile with visibility options
 */
export function DevelopPanel({
  adjustments,
  baselineAdjustments = null,
  onChange,
  onReset,
  onCopy,
  onPaste,
  hasClipboard = false,
  disabled = false,
  appliedProfileId: _appliedProfileId = null,
  onApplyProfile = null,
  onShowOriginal = null,
}) {
  // Tab state
  const [activeTab, setActiveTab] = useState("basic");

  // Collapsible section state (persisted in localStorage)
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem('develop-collapsed');
      return stored ? JSON.parse(stored) : { colorGrading: true };
    } catch {
      return { colorGrading: true };
    }
  });

  // Profile state (for backward compat with old dropdown)
  const [, setShowProfileDropdown] = useState(false);

  // Save Profile modal state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [newProfileName, setNewProfileName] = useState("");
  const [saveError, setSaveError] = useState(null);
  const [companyWide, setCompanyWide] = useState(false);
  // Profile type: 'delta' (adjustments only) or 'complete' (baseline + adjustments)
  const [profileType, setProfileType] = useState("delta");

  // Import Dstyle modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importName, setImportName] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [importError, setImportError] = useState(null);
  const [importing, setImporting] = useState(false);

  // Use hooks for profiles and permissions
  const {
    systemProfiles,
    companyProfiles,
    personalProfiles,
    loading: loadingProfiles,
    saveAsProfile,
    setDefault,
    importDstyle,
  } = useProfiles();
  const { canImportDstyle, canSetDefaultProfile } = usePermissions();

  // Slider change handler
  const handleSliderChange = (key) => (value) => {
    onChange({ ...adjustments, [key]: value });
  };

  // Sharpening change handler
  const handleSharpeningChange = useCallback((key, value) => {
    onChange((prev) => {
      const sharp = prev.sharpening || { amount: 0, radius: 0.5 };
      return {
        ...prev,
        sharpening: { ...sharp, [key]: value },
      };
    });
  }, [onChange]);

  // Color grading change handler
  const handleColorGradingChange = useCallback((zone, channel, value) => {
    onChange((prev) => {
      const cg = prev.color_grading || { ...DEFAULT_COLOR_GRADING };
      return {
        ...prev,
        color_grading: {
          ...cg,
          [zone]: { ...cg[zone], [channel]: value },
        },
      };
    });
  }, [onChange]);

  // Toggle collapsible section
  const toggleSection = useCallback((key) => {
    setCollapsed((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('develop-collapsed', JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  }, []);

  // Tone curve change handler
  const handleToneCurveChange = useCallback((newPoints) => {
    onChange((prev) => ({ ...prev, tone_curve: newPoints }));
  }, [onChange]);

  // Tone curve reset handler
  const handleToneCurveReset = useCallback(() => {
    onChange((prev) => ({ ...prev, tone_curve: null }));
  }, [onChange]);

  // Check if color grading has been modified
  const isColorGradingModified = useMemo(() => {
    const cg = adjustments.color_grading || DEFAULT_COLOR_GRADING;
    const def = DEFAULT_COLOR_GRADING;
    for (const zone of ['lift', 'gamma', 'gain']) {
      for (const ch of ['r', 'g', 'b']) {
        const defVal = zone === 'lift' ? 0 : 1;
        if ((cg[zone]?.[ch] ?? defVal) !== (def[zone]?.[ch] ?? defVal)) return true;
      }
    }
    return false;
  }, [adjustments.color_grading]);

  // Get current sharpening values
  const sharpening = useMemo(() => {
    if (!adjustments.sharpening) return { amount: 0, radius: 0.5 };
    return typeof adjustments.sharpening === "object"
      ? adjustments.sharpening
      : { amount: adjustments.sharpening, radius: 0.5 };
  }, [adjustments.sharpening]);

  // Get current color grading values
  const colorGrading = useMemo(() => {
    return adjustments.color_grading || DEFAULT_COLOR_GRADING;
  }, [adjustments.color_grading]);

  // Handle profile selection (applies adjustments)
  const handleSelectProfile = (profile) => {
    if (onApplyProfile && profile.adjustments) {
      onApplyProfile(profile);
    }
    setShowProfileDropdown(false);
  };

  // Determine if we have a baseline to combine with
  const hasBaseline = baselineAdjustments && Object.keys(baselineAdjustments).length > 0;

  // Compute what will be saved based on profile type
  const adjustmentsToSave = useMemo(() => {
    if (profileType === "complete" && hasBaseline) {
      return combineAdjustments(baselineAdjustments, adjustments);
    }
    return adjustments;
  }, [profileType, hasBaseline, baselineAdjustments, adjustments]);

  // Summary of what will be saved
  const adjustmentSummary = useMemo(() => {
    return formatAdjustmentSummary(adjustmentsToSave);
  }, [adjustmentsToSave]);

  // Save as profile handler
  const handleSaveAsProfile = async () => {
    const name = newProfileName.trim();
    if (!name || name.length < 3) {
      setSaveError("Name must be at least 3 characters");
      return;
    }

    try {
      setSavingProfile(true);
      setSaveError(null);
      const result = await saveAsProfile(name, adjustmentsToSave, companyWide, profileType);
      if (!result.ok) {
        setSaveError(result.message || "Failed to save profile");
        return;
      }
      setNewProfileName("");
      setCompanyWide(false);
      setProfileType("delta");
      setShowSaveModal(false);
    } catch (err) {
      setSaveError(err.response?.data?.detail || "Failed to save profile");
    } finally {
      setSavingProfile(false);
    }
  };

  // Import file change handler
  const handleImportFile = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImportFile(file);
      const defaultName = file.name.replace(/\.dstyle$/i, "");
      setImportName(defaultName);
    }
  }, []);

  // Import dstyle handler
  const handleImportDstyle = async () => {
    if (!importFile || !importName.trim()) {
      setImportError("Please select a file and provide a name");
      return;
    }

    try {
      setImporting(true);
      setImportError(null);
      const content = await importFile.text();
      const result = await importDstyle(importName.trim(), content);
      if (!result.ok) {
        setImportError(result.message || "Failed to import profile");
        return;
      }
      setImportName("");
      setImportFile(null);
      setShowImportModal(false);
    } catch (err) {
      setImportError(err.message || "Failed to import profile");
    } finally {
      setImporting(false);
    }
  };

  // Set default handler
  const handleSetDefault = async (profileId) => {
    const result = await setDefault(profileId);
    if (!result.ok) {
      console.error("Failed to set default profile:", result.message);
    }
  };

  // Slider groups for Basic tab
  const sliderGroups = [
    {
      title: "Light",
      sliders: [
        { key: "exposure", label: "Exposure", min: -3, max: 3, step: 0.1 },
        { key: "contrast", label: "Contrast" },
        { key: "highlights", label: "Highlights" },
        { key: "shadows", label: "Shadows" },
        { key: "whites", label: "Whites" },
        { key: "blacks", label: "Blacks" },
      ],
    },
    {
      title: "Presence",
      sliders: [
        { key: "clarity", label: "Clarity" },
        { key: "dehaze", label: "Dehaze", min: 0 },
        { key: "vibrance", label: "Vibrance" },
        { key: "saturation", label: "Saturation" },
      ],
    },
    {
      title: "Color",
      sliders: [
        { key: "temperature", label: "Temp" },
        { key: "tint", label: "Tint" },
      ],
    },
  ];

  return (
    <div className="develop-panel">
      {/* Tab buttons */}
      <div className="develop-tabs">
        <button
          className={`tab-btn ${activeTab === "basic" ? "active" : ""}`}
          onClick={() => setActiveTab("basic")}
        >
          Basic
        </button>
        <button
          className={`tab-btn ${activeTab === "advanced" ? "active" : ""}`}
          onClick={() => setActiveTab("advanced")}
        >
          Advanced
        </button>
        <button
          className={`tab-btn ${activeTab === "profiles" ? "active" : ""}`}
          onClick={() => setActiveTab("profiles")}
        >
          Profiles
        </button>
      </div>

      {/* Header with actions (only show on Basic/Advanced tabs) */}
      {(activeTab === "basic" || activeTab === "advanced") && (
        <div className="develop-header">
          <div className="develop-actions">
            {/* Before button - only show when adjustments exist */}
            {!isDefaultAdjustments(adjustments) && onShowOriginal && (
              <button
                className="ghost small"
                onMouseDown={() => onShowOriginal(true)}
                onMouseUp={() => onShowOriginal(false)}
                onMouseLeave={() => onShowOriginal(false)}
                title="Compare with original (hold \ key)"
                disabled={disabled}
              >
                Before
              </button>
            )}
            <button
              className="ghost small"
              onClick={onCopy}
              title="Copy settings (Shift+Cmd+C)"
              disabled={disabled}
            >
              Copy
            </button>
            <button
              className="ghost small"
              onClick={onPaste}
              title="Paste settings (Shift+Cmd+V)"
              disabled={disabled || !hasClipboard}
            >
              Paste
            </button>
            <button
              className="ghost small"
              onClick={onReset}
              title="Reset all adjustments"
              disabled={disabled}
            >
              Reset
            </button>
          </div>
        </div>
      )}

      {/* Basic tab content */}
      {activeTab === "basic" && (
        <div className="develop-sliders">
          {sliderGroups.map((group) => (
            <div key={group.title} className="slider-group">
              <div className="slider-group-title">{group.title}</div>
              {group.sliders.map((slider) => (
                <AdjustmentSlider
                  key={slider.key}
                  label={slider.label}
                  value={adjustments[slider.key] ?? 0}
                  min={slider.min ?? -100}
                  max={slider.max ?? 100}
                  step={slider.step ?? 1}
                  defaultValue={0}
                  onChange={handleSliderChange(slider.key)}
                  disabled={disabled}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Advanced tab content */}
      {activeTab === "advanced" && (
        <div className="develop-sliders">
          {/* Tone Curve */}
          <div className="collapsible-section">
            <button
              className="collapsible-header"
              onClick={() => toggleSection("toneCurve")}
              aria-expanded={!collapsed.toneCurve}
            >
              <ChevronDownIcon
                className={`collapsible-chevron ${collapsed.toneCurve ? "collapsed" : ""}`}
              />
              <span className="slider-group-title" style={{ marginBottom: 0 }}>Tone Curve</span>
              <span className="collapsible-actions">
                {adjustments.tone_curve != null && (
                  <span
                    className="collapsible-reset"
                    onClick={(e) => { e.stopPropagation(); handleToneCurveReset(); }}
                    role="button"
                    tabIndex={0}
                  >
                    Reset
                  </span>
                )}
              </span>
            </button>
            {!collapsed.toneCurve && (
              <div className="collapsible-content">
                <ToneCurve
                  points={adjustments.tone_curve}
                  onChange={handleToneCurveChange}
                  disabled={disabled}
                />
              </div>
            )}
          </div>

          {/* Sharpening */}
          <div className="collapsible-section">
            <button
              className="collapsible-header"
              onClick={() => toggleSection("sharpening")}
              aria-expanded={!collapsed.sharpening}
            >
              <ChevronDownIcon
                className={`collapsible-chevron ${collapsed.sharpening ? "collapsed" : ""}`}
              />
              <span className="slider-group-title" style={{ marginBottom: 0 }}>Sharpening</span>
            </button>
            {!collapsed.sharpening && (
              <div className="collapsible-content">
                <AdjustmentSlider
                  label="Amount"
                  value={sharpening.amount}
                  min={0}
                  max={3}
                  step={0.1}
                  defaultValue={0}
                  onChange={(v) => handleSharpeningChange("amount", v)}
                  disabled={disabled}
                />
                <AdjustmentSlider
                  label="Radius"
                  value={sharpening.radius}
                  min={0.1}
                  max={3}
                  step={0.1}
                  defaultValue={0.5}
                  onChange={(v) => handleSharpeningChange("radius", v)}
                  disabled={disabled}
                />
              </div>
            )}
          </div>

          {/* Denoise */}
          <div className="collapsible-section">
            <button
              className="collapsible-header"
              onClick={() => toggleSection("denoise")}
              aria-expanded={!collapsed.denoise}
            >
              <ChevronDownIcon
                className={`collapsible-chevron ${collapsed.denoise ? "collapsed" : ""}`}
              />
              <span className="slider-group-title" style={{ marginBottom: 0 }}>Denoise</span>
              <span className="collapsible-badge">Applied on export</span>
            </button>
            {!collapsed.denoise && (
              <div className="collapsible-content">
                <AdjustmentSlider
                  label="Strength"
                  value={adjustments.denoise ?? 0}
                  min={0}
                  max={100}
                  step={1}
                  defaultValue={0}
                  onChange={handleSliderChange("denoise")}
                  disabled={disabled}
                />
              </div>
            )}
          </div>

          {/* Color Grading */}
          <div className="collapsible-section">
            <button
              className="collapsible-header"
              onClick={() => toggleSection("colorGrading")}
              aria-expanded={!collapsed.colorGrading}
            >
              <ChevronDownIcon
                className={`collapsible-chevron ${collapsed.colorGrading ? "collapsed" : ""}`}
              />
              <span className="slider-group-title" style={{ marginBottom: 0 }}>Color Grading</span>
              {isColorGradingModified && (
                <span className="collapsible-badge modified">Modified</span>
              )}
            </button>
            {!collapsed.colorGrading && (
              <div className="collapsible-content">
                {/* Shadow Tint */}
                <div className="slider-group">
                  <div className="slider-group-subtitle">Shadow Tint</div>
                  <div className="rgb-sliders">
                    <AdjustmentSlider
                      label="R"
                      value={colorGrading.lift.r}
                      min={-1}
                      max={1}
                      step={0.01}
                      defaultValue={0}
                      onChange={(v) => handleColorGradingChange("lift", "r", v)}
                      disabled={disabled}
                    />
                    <AdjustmentSlider
                      label="G"
                      value={colorGrading.lift.g}
                      min={-1}
                      max={1}
                      step={0.01}
                      defaultValue={0}
                      onChange={(v) => handleColorGradingChange("lift", "g", v)}
                      disabled={disabled}
                    />
                    <AdjustmentSlider
                      label="B"
                      value={colorGrading.lift.b}
                      min={-1}
                      max={1}
                      step={0.01}
                      defaultValue={0}
                      onChange={(v) => handleColorGradingChange("lift", "b", v)}
                      disabled={disabled}
                    />
                  </div>
                </div>

                {/* Midtone Tint */}
                <div className="slider-group">
                  <div className="slider-group-subtitle">Midtone Tint</div>
                  <div className="rgb-sliders">
                    <AdjustmentSlider
                      label="R"
                      value={colorGrading.gamma.r}
                      min={0.5}
                      max={2}
                      step={0.01}
                      defaultValue={1}
                      onChange={(v) => handleColorGradingChange("gamma", "r", v)}
                      disabled={disabled}
                    />
                    <AdjustmentSlider
                      label="G"
                      value={colorGrading.gamma.g}
                      min={0.5}
                      max={2}
                      step={0.01}
                      defaultValue={1}
                      onChange={(v) => handleColorGradingChange("gamma", "g", v)}
                      disabled={disabled}
                    />
                    <AdjustmentSlider
                      label="B"
                      value={colorGrading.gamma.b}
                      min={0.5}
                      max={2}
                      step={0.01}
                      defaultValue={1}
                      onChange={(v) => handleColorGradingChange("gamma", "b", v)}
                      disabled={disabled}
                    />
                  </div>
                </div>

                {/* Highlight Tint */}
                <div className="slider-group">
                  <div className="slider-group-subtitle">Highlight Tint</div>
                  <div className="rgb-sliders">
                    <AdjustmentSlider
                      label="R"
                      value={colorGrading.gain.r}
                      min={0}
                      max={2}
                      step={0.01}
                      defaultValue={1}
                      onChange={(v) => handleColorGradingChange("gain", "r", v)}
                      disabled={disabled}
                    />
                    <AdjustmentSlider
                      label="G"
                      value={colorGrading.gain.g}
                      min={0}
                      max={2}
                      step={0.01}
                      defaultValue={1}
                      onChange={(v) => handleColorGradingChange("gain", "g", v)}
                      disabled={disabled}
                    />
                    <AdjustmentSlider
                      label="B"
                      value={colorGrading.gain.b}
                      min={0}
                      max={2}
                      step={0.01}
                      defaultValue={1}
                      onChange={(v) => handleColorGradingChange("gain", "b", v)}
                      disabled={disabled}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Profiles tab content */}
      {activeTab === "profiles" && (
        <div className="profiles-tab">
          {/* Import button for managers */}
          {/* Save as Profile button */}
          <div className="profile-actions-bar">
            <button
              className="btn-save-profile"
              onClick={() => setShowSaveModal(true)}
            >
              Save Current as Profile...
            </button>
          </div>

          {/* Loading state */}
          {loadingProfiles && (
            <div className="profiles-loading">Loading profiles...</div>
          )}

          {/* System Profiles */}
          {systemProfiles.length > 0 && (
            <div className="profile-group">
              <div className="profile-group-header">System Profiles</div>
              {systemProfiles.map((p) => (
                <div
                  key={p.id}
                  className="profile-item"
                  onClick={() => handleSelectProfile(p)}
                >
                  <span className="profile-name">{p.name}</span>
                </div>
              ))}
            </div>
          )}

          {/* Company Profiles */}
          {companyProfiles.length > 0 && (
            <div className="profile-group">
              <div className="profile-group-header">Company Profiles</div>
              {companyProfiles.map((p) => (
                <div
                  key={p.id}
                  className="profile-item"
                  onClick={() => handleSelectProfile(p)}
                >
                  <span className="profile-name">{p.name}</span>
                  {p.is_default && <span className="profile-badge">Default</span>}
                  {canSetDefaultProfile && !p.is_default && (
                    <button
                      className="btn-set-default"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSetDefault(p.id);
                      }}
                    >
                      Set Default
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Personal Profiles */}
          {personalProfiles.length > 0 && (
            <div className="profile-group">
              <div className="profile-group-header">My Profiles</div>
              {personalProfiles.map((p) => (
                <div
                  key={p.id}
                  className="profile-item"
                  onClick={() => handleSelectProfile(p)}
                >
                  <span className="profile-name">{p.name}</span>
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!loadingProfiles &&
            systemProfiles.length === 0 &&
            companyProfiles.length === 0 &&
            personalProfiles.length === 0 && (
              <p className="muted-text" style={{ textAlign: "center", padding: 20 }}>
                No profiles available. Save your current adjustments as a
                profile to get started.
              </p>
            )}
        </div>
      )}

      {/* Save Profile Modal - rendered via portal for proper centering */}
      {showSaveModal && createPortal(
        <div className="modal-backdrop" onClick={() => setShowSaveModal(false)}>
          <div
            className="modal-card person-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h4>Save as Profile</h4>
              <button
                className="modal-close"
                onClick={() => setShowSaveModal(false)}
              >
                <XMarkIcon className="icon" />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid" style={{ gridTemplateColumns: "1fr" }}>
                <div className="form-field">
                  <label>Profile Name</label>
                  <input
                    type="text"
                    value={newProfileName}
                    onChange={(e) => setNewProfileName(e.target.value)}
                    placeholder="My Custom Profile"
                    autoFocus
                    disabled={savingProfile}
                  />
                </div>

                {/* Profile type selector - only show if we have a baseline */}
                {hasBaseline && (
                  <div className="form-field" style={{ marginTop: 12 }}>
                    <label>What to Save</label>
                    <div className="profile-type-options">
                      <div
                        className={`profile-type-option ${profileType === "delta" ? "selected" : ""}`}
                        onClick={() => !savingProfile && setProfileType("delta")}
                      >
                        <div className="option-radio">
                          <input
                            type="radio"
                            name="profile-type"
                            checked={profileType === "delta"}
                            onChange={() => setProfileType("delta")}
                            disabled={savingProfile}
                          />
                        </div>
                        <div className="option-content">
                          <div className="option-title">My adjustments only</div>
                          <div className="option-desc">Saves your tweaks as a reusable layer</div>
                        </div>
                      </div>
                      <div
                        className={`profile-type-option ${profileType === "complete" ? "selected" : ""}`}
                        onClick={() => !savingProfile && setProfileType("complete")}
                      >
                        <div className="option-radio">
                          <input
                            type="radio"
                            name="profile-type"
                            checked={profileType === "complete"}
                            onChange={() => setProfileType("complete")}
                            disabled={savingProfile}
                          />
                        </div>
                        <div className="option-content">
                          <div className="option-title">Complete look</div>
                          <div className="option-desc">Combines base edit with your adjustments</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Adjustment summary */}
                {adjustmentSummary && (
                  <div className="adjustment-summary">
                    <span className="summary-label">Includes:</span> {adjustmentSummary}
                  </div>
                )}

                <div className="form-field" style={{ marginTop: 12 }}>
                  <label>Visibility</label>
                  <div className="profile-type-options">
                    <div
                      className={`profile-type-option ${!companyWide ? "selected" : ""}`}
                      onClick={() => !savingProfile && setCompanyWide(false)}
                    >
                      <div className="option-radio">
                        <input
                          type="radio"
                          name="profile-visibility"
                          checked={!companyWide}
                          onChange={() => setCompanyWide(false)}
                          disabled={savingProfile}
                        />
                      </div>
                      <div className="option-content">
                        <div className="option-title">Personal</div>
                        <div className="option-desc">Only visible to you</div>
                      </div>
                    </div>
                    <div
                      className={`profile-type-option ${companyWide ? "selected" : ""}`}
                      onClick={() => !savingProfile && setCompanyWide(true)}
                    >
                      <div className="option-radio">
                        <input
                          type="radio"
                          name="profile-visibility"
                          checked={companyWide}
                          onChange={() => setCompanyWide(true)}
                          disabled={savingProfile}
                        />
                      </div>
                      <div className="option-content">
                        <div className="option-title">Company-wide</div>
                        <div className="option-desc">Visible to all company users</div>
                      </div>
                    </div>
                  </div>
                </div>

                {saveError && (
                  <div style={{ color: "#ff6b6b", fontSize: 13, marginTop: 8 }}>
                    {saveError}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="ghost"
                onClick={() => setShowSaveModal(false)}
                disabled={savingProfile}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAsProfile}
                disabled={savingProfile || !newProfileName.trim()}
              >
                {savingProfile ? "Saving..." : "Save Profile"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Import Dstyle Modal - rendered via portal for proper centering */}
      {/* Preview notice - only shown when adjustments are non-default */}
      {!isDefaultAdjustments(adjustments) && (
        <div className="develop-notice">
          <svg
            className="develop-notice-icon"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <span>Preview may differ slightly from final export.</span>
        </div>
      )}

      <style>{`
        .develop-panel {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .develop-tabs {
          display: flex;
          gap: 4px;
          background: var(--panel);
          padding: 4px;
          border-radius: 8px;
        }

        .develop-tabs .tab-btn {
          flex: 1;
          padding: 8px 12px;
          background: transparent;
          border: none;
          color: var(--muted);
          cursor: pointer;
          border-radius: 6px;
          font-size: 0.8rem;
          transition: all 150ms ease;
        }

        .develop-tabs .tab-btn:hover {
          color: var(--text);
        }

        .develop-tabs .tab-btn.active {
          background: var(--accent);
          color: #041408;
        }

        .develop-header {
          display: flex;
          justify-content: flex-end;
        }

        .develop-actions {
          display: flex;
          gap: 6px;
        }

        .develop-actions button {
          padding: 6px 10px;
          font-size: 0.75rem;
        }

        .develop-sliders {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .slider-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .slider-group-title {
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--muted);
          margin-bottom: 4px;
        }

        .rgb-sliders {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .muted-text {
          color: var(--muted);
          font-size: 0.75rem;
          font-style: italic;
          margin-bottom: 8px;
        }

        /* Collapsible sections */
        .collapsible-section {
          border: 1px solid var(--border);
          border-radius: 6px;
          overflow: hidden;
        }

        .collapsible-header {
          display: flex;
          align-items: center;
          gap: 6px;
          width: 100%;
          padding: 8px 10px;
          background: transparent;
          border: none;
          color: var(--text);
          cursor: pointer;
          text-align: left;
          transition: background 150ms ease;
        }

        .collapsible-header:hover {
          background: rgba(255, 255, 255, 0.03);
        }

        .collapsible-chevron {
          width: 14px;
          height: 14px;
          color: var(--muted);
          flex-shrink: 0;
          transition: transform 200ms ease;
        }

        .collapsible-chevron.collapsed {
          transform: rotate(-90deg);
        }

        .collapsible-actions {
          margin-left: auto;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .collapsible-reset {
          font-size: 0.7rem;
          color: var(--muted);
          cursor: pointer;
          padding: 2px 6px;
          border-radius: 4px;
          transition: color 150ms ease;
        }

        .collapsible-reset:hover {
          color: var(--accent);
        }

        .collapsible-badge {
          font-size: 0.6rem;
          color: var(--muted);
          padding: 1px 6px;
          border: 1px solid var(--border);
          border-radius: 4px;
          margin-left: auto;
          white-space: nowrap;
        }

        .collapsible-badge.modified {
          color: var(--accent);
          border-color: var(--accent-dim, rgba(102, 255, 183, 0.3));
        }

        .collapsible-content {
          padding: 4px 10px 10px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        /* Color grading subtitle */
        .slider-group-subtitle {
          font-size: 0.7rem;
          color: var(--muted);
          margin-bottom: 2px;
          opacity: 0.8;
        }

        /* Tone curve container */
        .tone-curve-container {
          width: 100%;
          border: 1px solid var(--border);
          border-radius: 4px;
          overflow: hidden;
        }

        /* Profiles tab */
        .profiles-tab {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .profile-actions-bar {
          display: flex;
          gap: 8px;
        }

        .btn-import,
        .btn-save-profile {
          flex: 1;
          padding: 10px 12px;
          background: transparent;
          border: 1px solid var(--border);
          color: var(--text);
          border-radius: 8px;
          cursor: pointer;
          font-size: 0.85rem;
          transition: all 150ms ease;
        }

        .btn-import:hover,
        .btn-save-profile:hover {
          border-color: var(--accent);
          color: var(--accent);
        }

        .profiles-loading {
          text-align: center;
          padding: 20px;
          color: var(--muted);
          font-size: 0.85rem;
        }

        .profile-group {
          margin-bottom: 12px;
        }

        .profile-group-header {
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--muted);
          margin-bottom: 8px;
          padding-bottom: 4px;
          border-bottom: 1px solid var(--border);
        }

        .profile-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 6px;
          margin-bottom: 6px;
          cursor: pointer;
          transition: all 150ms ease;
        }

        .profile-item:hover {
          border-color: var(--accent);
          background: var(--panel-strong);
        }

        .profile-name {
          flex: 1;
          font-size: 0.85rem;
          color: var(--text);
        }

        .profile-badge {
          padding: 2px 8px;
          background: var(--accent);
          color: #041408;
          border-radius: 4px;
          font-size: 0.65rem;
          font-weight: 600;
          text-transform: uppercase;
        }

        .btn-set-default {
          padding: 4px 8px;
          background: transparent;
          border: 1px solid var(--border);
          color: var(--muted);
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.7rem;
          transition: all 150ms ease;
        }

        .btn-set-default:hover {
          border-color: var(--accent);
          color: var(--accent);
        }

        .develop-notice {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          margin-top: 16px;
          padding: 10px 12px;
          background: rgba(102, 183, 255, 0.08);
          border: 1px solid rgba(102, 183, 255, 0.2);
          border-radius: 6px;
          font-size: 0.75rem;
          line-height: 1.4;
          color: var(--muted);
        }

        .develop-notice-icon {
          flex-shrink: 0;
          color: rgba(102, 183, 255, 0.7);
          margin-top: 1px;
        }

        /* Profile type selector */
        .profile-type-options {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-top: 8px;
        }

        .profile-type-option {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px;
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 8px;
          cursor: pointer;
          transition: all 150ms ease;
        }

        .profile-type-option:hover {
          border-color: var(--accent-dim);
        }

        .profile-type-option.selected {
          border-color: var(--accent);
          background: rgba(102, 255, 183, 0.05);
        }

        .profile-type-option .option-radio {
          padding-top: 2px;
        }

        .profile-type-option .option-radio input {
          accent-color: var(--accent);
        }

        .profile-type-option .option-content {
          flex: 1;
        }

        .profile-type-option .option-title {
          font-size: 14px;
          font-weight: 500;
          color: var(--text);
          margin-bottom: 2px;
        }

        .profile-type-option .option-desc {
          font-size: 12px;
          color: var(--muted);
        }

        /* Adjustment summary */
        .adjustment-summary {
          margin-top: 12px;
          padding: 10px 12px;
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 6px;
          font-size: 13px;
          color: var(--muted);
        }

        .adjustment-summary .summary-label {
          color: var(--text);
          font-weight: 500;
        }
      `}</style>
    </div>
  );
}
